/**
 * Who may see a program, and who may enter what on it.
 *
 * See `docs/program-access.md`. The short version: a program **declares its own
 * audiences**, so adding a plan does not mean editing everybody's grant, and a
 * plan that advisors should not know exists simply does not list them.
 *
 * ## Audience, level and scope are three separate things
 *
 * Collapsing any two of them loses a case somebody wants:
 *
 * - **Audience** is *who*: a role, a capability, or a relationship to the
 *   student. Relationships are the interesting ones, because they are already
 *   in the data and need no list anybody maintains.
 * - **Level** is *how much*. Four values, because "may tick the box the policy
 *   marked as theirs" and "may place a hold" are genuinely different powers and
 *   an advisor is meant to have the first without the second.
 * - **Scope** is *which students*. `all` for the office, `own` for a
 *   relationship. Naming the students on a grant is a third case, deliberately
 *   not built yet — see the note on `"grant"` below.
 *
 * ## This is UI gating and it is not the boundary
 *
 * `firestore.rules` decides who may write `program_records`, and it decides it
 * coarsely: it cannot tell a flag write from a hold write while both live
 * inside one student's subtree of one document. So an advisor who bypassed the
 * screen could still write a hold. What follows stops the honest mistake, not
 * the determined one, and `docs/program-access.md` §7 has the fix that would
 * make it a real boundary.
 *
 * The same posture applies to `scope`: filtering a rollup the viewer may read
 * whole is a display decision, not access control. `docs/senior-privileges.md`
 * §6 argues the threat model this is answering — defence against accidental
 * exposure, by people who already hold the underlying data in the SIS.
 */
import { CAPABILITIES, CAPABILITY_LABELS, hasCapability } from "./access.js";
import type { AdminAccessEntry } from "./access.js";
import { isHonorsAdmin } from "./honors.js";
import type {
  Criterion,
  Program,
  ProgramPopulation,
  ProgramRole,
} from "./programs.js";

/**
 * How much of a program one audience gets.
 *
 * `hidden` is the absence of an entry rather than a value anybody writes, and
 * it has to mean *not listed anywhere* — a greyed-out row announces that the
 * plan exists, which for some plans is the whole thing being avoided.
 */
export type ProgramLevel = "hidden" | "view" | "contribute" | "manage";

/**
 * How each level should read to whoever is authoring a policy.
 *
 * Phrased as what the person may *do*, not as what the system permits, because
 * the author is deciding about people: "may tick the boxes marked for them" is
 * a sentence about a job, and "contribute" on its own is a sentence about a
 * schema.
 */
export const PROGRAM_LEVEL_LABELS = {
  hidden: {
    label: "-",
    description: "They don't interact with the program.",
  },
  view: {
    label: "Viewer",
    description: "They can see student statuses but cannot enter data.",
  },
  contribute: {
    label: "Contributor",
    description: "They can enter specific data and view student statuses.",
  },
  manage: {
    label: "Manager",
    description: "They can enter specific data and manage the program.",
  },
} satisfies Record<ProgramLevel, { label: string; description: string }>;

/**
 * Which students, for an audience that has access at all.
 *
 * `own` resolves through the relationship the audience names — an advisor's
 * advisees, a teacher's students — so nobody maintains a list and it stays
 * correct when an advisory or a schedule changes.
 */
export type ProgramScope = "all" | "own";

/**
 * One line of a program's access block.
 *
 * `audience` is a string rather than a union so that adding a relationship
 * later — `relationship:coach`, once athletic rosters are imported — is data
 * rather than a schema change. An audience nothing recognises matches nobody,
 * which is the safe direction to fail.
 */
export interface ProgramAccessEntry {
  audience: string;
  level: Exclude<ProgramLevel, "hidden">;
  scope: ProgramScope;
}

/**
 * The four kinds of person a program can name.
 *
 * A closed list, and deliberately short. The alternative — letting each program
 * invent its own contributor types — ends with seven checkboxes and seven
 * bespoke audiences, and nobody able to answer "who may tick that one?" without
 * opening the policy. Four covers the cases the school actually has, and a
 * criterion names which of the four owns it.
 *
 * A role is **not** a capability. A capability is what `firestore.rules` checks
 * to decide whether a write is allowed at all; a role is what a screen uses to
 * decide which control to draw. They have to agree, because a control drawn for
 * somebody the rules will refuse is worse than no control — see
 * `viewerCeiling()` below, which is what keeps them agreeing.
 */
export type { ProgramRole };

/** The closed list, so a typo in a policy is findable. */
export const PROGRAM_ROLES = {
  ADMINISTRATOR: "administrator",
  OFFICE: "office",
  ADVISOR: "advisor",
  TEACHER: "teacher",
} as const;

/** How each role should read to whoever is authoring a policy. */
export const PROGRAM_ROLE_LABELS = {
  administrator: {
    label: "Administrator",
    description:
      "Runs the program. The only role that may place and lift holds.",
  },
  office: {
    label: "Office staff",
    description:
      "Enters what comes across the desk — a form received, a signature on file.",
  },
  advisor: {
    label: "Advisor",
    description: "The advisor of record, for their own advisees only.",
  },
  teacher: {
    label: "Teacher",
    description: "Answers questions from the students they teach. Reads only.",
  },
} satisfies Record<ProgramRole, { label: string; description: string }>;

/**
 * How somebody comes to fill each role.
 *
 * The grant editor and the policy editor are two screens one administrator uses
 * in one sitting, and they were speaking different languages: the policy named
 * roles, the grant named capabilities, and nothing said which capability made
 * somebody "the office". This is the bridge, and it interpolates the capability
 * labels rather than restating them so the two cannot drift.
 *
 * Only `office` is granted by hand at all now. An administrator is one by their
 * access role; an advisor and a teacher are each one by a real Aspen roster --
 * an Advisory section of record for the advisor, any class at all for the
 * teacher -- and neither needs a capability ticked anywhere. `teacher` used to
 * be recognized this way already (`isRole`) while still needing a capability
 * before any program could actually show them anything; both roles now work
 * the same way end to end, with no manual grant in between.
 */
export const PROGRAM_ROLE_SOURCES = {
  administrator: `From the access role above — an admin or super at the high school. Not a capability, and not granted here.`,
  office: `Tick "${CAPABILITY_LABELS[CAPABILITIES.PROGRAMS_WRITE].label}" below, or "${CAPABILITY_LABELS[CAPABILITIES.PROGRAMS_READ].label}" for somebody who should see and enter nothing. Name their office too if a program is only for some offices.`,
  advisor: `From their Advisory section in Aspen. Nobody grants this by hand — it follows the nightly roster import, the same way a teacher's own roster does.`,
  teacher: `From their class roster. No capability needed any more — a program that lists "Teacher" shows it to anyone the roster says teaches that student.`,
} satisfies Record<ProgramRole, string>;

/**
 * Where each role starts when a policy has not said otherwise.
 *
 * Defaults, not limits. A policy may raise a role as well as lower it — a
 * school that wants its office placing holds can say so, and one that wants
 * advisors running a program outright can say that too. What matters is that it
 * is **written down on the program** rather than implied by which capability
 * somebody happens to hold.
 *
 * The office defaults to `contribute` because that is what the job usually is:
 * ticking a box when a form arrives. It used to resolve to `manage`, which is
 * how the capability the criteria editor labelled "Main office" could lift a
 * discipline hold an assistant principal had placed. Making that the default
 * rather than the ceiling is the fix; making it unsayable would be a different
 * and worse bug.
 *
 * `scope` is structural for the two roles that have no relationship to resolve:
 * an office account is nobody's advisor and teaches nothing, so `own` would
 * resolve to no students at all. For the two that do, it is a real choice and
 * the editor offers it.
 */
export const PROGRAM_ROLE_DEFAULTS = {
  administrator: { level: "manage", scope: "all" },
  office: { level: "contribute", scope: "all" },
  advisor: { level: "contribute", scope: "own" },
  teacher: { level: "view", scope: "own" },
} satisfies Record<
  ProgramRole,
  { level: Exclude<ProgramLevel, "hidden">; scope: ProgramScope }
>;

/** The audience string a role is written as in an access block. */
export function roleAudience(role: ProgramRole): string {
  return `role:${role}`;
}

/**
 * The audience string for one named office's staff — `office:Athletics office`.
 *
 * An office audience is the office role narrowed to the people whose grant
 * names that office. So it has the office's powers and the office's scope, and
 * the access editor still has four rows: it is a filter on the office row, not
 * a fifth kind of person.
 */
export function officeAudience(office: string): string {
  return `office:${office}`;
}

/** The office an `office:` audience names, if it is one. */
export function audienceOffice(audience: string): string | null {
  return audience.startsWith("office:")
    ? audience.slice("office:".length) || null
    : null;
}

/**
 * The role an audience string names, if any.
 *
 * Understands the pre-role spellings as well, so an access block written before
 * the four roles existed — including `DEFAULT_PROGRAM_ACCESS` as it shipped —
 * keeps meaning what it meant and nothing has to be migrated. An audience
 * nothing recognises matches nobody, which is the safe direction to fail.
 */
export function audienceRole(audience: string): ProgramRole | null {
  if (audience.startsWith("role:")) {
    const named = audience.slice("role:".length);
    if (named in PROGRAM_ROLE_LABELS) return named as ProgramRole;
    // The original spelling of "an administrator of the high school".
    if (named === "hs-admin") return "administrator";
    return null;
  }
  if (audienceOffice(audience)) return "office";
  if (audience === `capability:${CAPABILITIES.PROGRAMS_WRITE}`) return "office";
  if (audience === `capability:${CAPABILITIES.PROGRAMS_READ}`) return "office";
  if (audience === `capability:${CAPABILITIES.PROGRAMS_ADVISOR}`)
    return "advisor";
  return null;
}

/**
 * What a program with no access block means.
 *
 * Every role the app knows, at its own default. This is deliberately generous
 * about *who is listed* and exact about *what they may do* — a school that has
 * not yet authored an access block should find the program working for
 * everybody it plausibly concerns, and should still not find the office able to
 * lift a discipline hold.
 */
export const DEFAULT_PROGRAM_ACCESS: ProgramAccessEntry[] = (
  Object.keys(PROGRAM_ROLE_LABELS) as ProgramRole[]
).map((role) => ({
  audience: roleAudience(role),
  ...PROGRAM_ROLE_DEFAULTS[role],
}));

/**
 * Whoever is about to look, as the app knows them.
 *
 * The *signed-in* user, never the impersonated one: Firestore rules apply to
 * the account holding the token, so a control gated on the person being
 * viewed-as would offer a write certain to fail.
 */
export interface ProgramViewer {
  /** The signed-in user's row in `lookups/admins`, if they have one. */
  entry?: Pick<AdminAccessEntry, "capabilities" | "role" | "offices"> | null;
  role?: string;
  schoolId?: string;
  /**
   * The viewer's own staff record. Display-only now -- `advisorNamesStaff()`
   * matched it against the denormalized `homeroom_advisor` string, which was
   * how advisor-ness used to be decided; `advisedStudentIds` below is the
   * roster-derived replacement for that gate, kept around because a name is
   * still useful to show.
   */
  staff?: { first_name?: string; last_name?: string };
  /** Student ids the viewer teaches, for `role:teacher`. */
  taughtStudentIds?: readonly string[];
  /**
   * Student ids the viewer advises, for `role:advisor`.
   *
   * Derived from the signed-in (or impersonated) staff member's own Advisory
   * section in Aspen -- the real, ID-based relationship the course-sections
   * export already carries a `teacher_id` for -- rather than matched against
   * the free-text `homeroom_advisor` string by name. See
   * `isAdvisorySection()` in `sections.ts` and `currentProgramViewer()` in
   * `webapp/src/lib/state/programViewer.svelte.ts`, which builds this the
   * same way `taughtStudentIds` is built, from the same roster document.
   */
  advisedStudentIds?: readonly string[];
  /**
   * What the viewer's roster says about each student they teach or advise --
   * the `__student_summaries` the importer already folds onto the roster doc.
   *
   * This is what lets a relationship role mean *on this program*: a teacher is
   * a teacher of a program only if they teach somebody it could include. Without
   * it every rostered teacher was a teacher of every program, and an HS teacher
   * was offered MS athletics eligibility onto a table with nobody in it. Absent
   * means unknown, and nothing is narrowed.
   */
  students?: Readonly<Record<string, RelatedStudent>>;
}

/** The population-relevant facts about one student, as a roster summary carries them. */
export interface RelatedStudent {
  grade_level?: number;
  school_id?: string;
  cohort?: string;
}

const LEVEL_ORDER: Record<ProgramLevel, number> = {
  hidden: 0,
  view: 1,
  contribute: 2,
  manage: 3,
};

/**
 * An HS administrator, who gets program access by role rather than by grant.
 *
 * Mirrors `isProgramAdmin()` in `firestore.rules`, which delegates to the same
 * honors predicate for the same reason: the condition is "super, or an admin
 * scoped to HS or to nothing", and two copies of a predicate about who runs the
 * high school would drift.
 */
export function isProgramAdmin(viewer: ProgramViewer): boolean {
  return isHonorsAdmin(viewer.role, viewer.schoolId);
}

/**
 * Whether this audience includes this viewer.
 *
 * A role audience asks the role. An office audience asks the office role and
 * then whether the viewer's grant names that office — so an account in no
 * office at all still matches `role:office`, as it always has, and matches no
 * named office.
 */
export function matchesAudience(
  audience: string,
  viewer: ProgramViewer,
): boolean {
  const role = audienceRole(audience);
  if (!role || !isRole(role, viewer)) return false;
  const office = audienceOffice(audience);
  return !office || (viewer.entry?.offices ?? []).includes(office);
}

/**
 * The offices this program is for, or `undefined` for every office.
 *
 * What `saveProgram` writes onto the program as `offices`, for
 * `firestore.rules` to check. Any unnamed office audience — `role:office`, or
 * the capability spellings that predate roles, or no access block at all —
 * means every office, because that is what those have always meant. An empty
 * list means the program names no office.
 */
export function programOffices(
  program: Pick<Program, "access">,
): string[] | undefined {
  const named = new Set<string>();
  for (const entry of programAccessEntries(program)) {
    if (audienceRole(entry.audience) !== "office") continue;
    const office = audienceOffice(entry.audience);
    if (!office) return undefined;
    named.add(office);
  }
  return [...named].sort();
}

/**
 * Every office anybody works in, for the two editors to offer.
 *
 * Read off the grants that already exist rather than kept as a list of its
 * own: an office is real once somebody is in it, and a second list would be
 * one more thing to keep in step with the first.
 */
export function knownOffices(
  entries: Iterable<Pick<AdminAccessEntry, "offices"> | null | undefined>,
): string[] {
  const names = new Set<string>();
  for (const entry of entries)
    for (const office of entry?.offices ?? [])
      if (office.trim()) names.add(office.trim());
  return [...names].sort((a, b) => a.localeCompare(b));
}

/**
 * Whether this viewer may ask for an evaluation now rather than wait for the
 * schedule.
 *
 * Mirrors the `create` rule on `program_evaluation_jobs`. The office is on the
 * list because it is the one adding students to a roster, and a roster it just
 * uploaded reads "not yet checked" until somebody runs a check — but only an
 * office the program is for. An advisor is not: they manage their own
 * advisees, not who is in the program.
 */
export function canRequestEvaluation(
  viewer: ProgramViewer,
  program: Pick<Program, "access">,
): boolean {
  if (isProgramAdmin(viewer)) return true;
  if (!hasCapability(viewer.entry, CAPABILITIES.PROGRAMS_WRITE)) return false;
  const offices = programOffices(program);
  return (
    !offices ||
    offices.some((office) => (viewer.entry?.offices ?? []).includes(office))
  );
}

/**
 * Whether this viewer is one of these people at all.
 *
 * Each role is recognised by the thing that is already true about the person,
 * never by a list somebody maintains: an administrator by the predicate the
 * rules use, the office by the capability its writes need, an advisor and a
 * teacher by a real Aspen roster -- an Advisory section of record for one, any
 * class at all for the other. `scope: "own"` is what narrows a relationship
 * role to *which* students, which is why this can answer "does this program
 * concern me at all" without loading a single one.
 */
export function isRole(role: ProgramRole, viewer: ProgramViewer): boolean {
  switch (role) {
    case "administrator":
      return isProgramAdmin(viewer);
    // The office writes, so it is recognised by the capability
    // `firestore.rules` will check when it does. Recognising it any other way
    // would draw boxes that fail on save.
    case "office":
      return (
        hasCapability(viewer.entry, CAPABILITIES.PROGRAMS_WRITE) ||
        hasCapability(viewer.entry, CAPABILITIES.PROGRAMS_READ)
      );
    case "advisor":
      return Boolean(viewer.advisedStudentIds?.length);
    case "teacher":
      return Boolean(viewer.taughtStudentIds?.length);
  }
}

/** Every role this viewer is, in the order the roles are declared. */
export function viewerRoles(viewer: ProgramViewer): ProgramRole[] {
  return (Object.keys(PROGRAM_ROLE_LABELS) as ProgramRole[]).filter((role) =>
    isRole(role, viewer),
  );
}

/**
 * The most this *account* could do, whatever a policy offers it.
 *
 * The one thing that is not a policy decision, because it is not this app's
 * decision at all: `firestore.rules` gates reading `program_status` and writing
 * `program_records` on something true about the account. A program granting
 * `contribute` to somebody with none of that would draw a checkbox in every
 * flag column and fail on all of them, and one granting anything to an account
 * the rules would refuse outright would draw a screen whose data never loads.
 *
 * An advisor or a teacher reaches this ceiling by their Aspen roster alone, no
 * capability needed -- `firestore.rules` checks the same roster-derived fact
 * (see `docs/program-access.md`'s note on `lookups/staff_roles`), so a control
 * drawn for either of them is one the rules will actually accept. The office
 * still needs a capability, because an office account is recognised by nothing
 * else.
 *
 * So this is a ceiling and never a second opinion — it only lowers what the
 * access block said, and only to what a save would actually survive. Note that
 * it stops well short of the hold controls: the rules cannot tell a hold write
 * from a flag write inside one student's subtree, so anybody who may write at
 * all could write a hold. Which of them is *offered* the control is exactly
 * what the access block decides, and `docs/program-access.md` §7 step 5 is the
 * migration that would make it a boundary rather than a screen.
 */
function viewerCeiling(viewer: ProgramViewer): ProgramLevel {
  if (isProgramAdmin(viewer)) return "manage";
  if (
    hasCapability(viewer.entry, CAPABILITIES.PROGRAMS_WRITE) ||
    Boolean(viewer.advisedStudentIds?.length) ||
    Boolean(viewer.taughtStudentIds?.length)
  )
    return "manage";
  if (hasCapability(viewer.entry, CAPABILITIES.PROGRAMS_READ)) return "view";
  // No capability and no roster at all cannot read `program_status`, so there
  // is no screen to show them — whatever role they would otherwise fill.
  return "hidden";
}

/**
 * Per-role read/write gates for `firestore.rules`, flattened off `access` the
 * same way `programOffices()` flattens office names -- rules cannot loop over
 * a list of maps to find the `role:advisor` / `role:teacher` rows and their
 * levels. `saveProgram` writes the result onto the program as `advisor_read`,
 * `advisor_write`, `teacher_read` and `teacher_write`; nothing else should.
 *
 * Absent means `DEFAULT_PROGRAM_ACCESS` (a program saved before this field
 * existed), which already grants advisors `contribute` and teachers `view` --
 * so the rules' own fallback for a missing field has to match that, not
 * "nothing", or every program ever saved before this shipped would silently
 * lock out its advisors and teachers until somebody re-saved it.
 */
export function programRoleAccess(
  program: Pick<Program, "access">,
): {
  advisor_read: boolean;
  advisor_write: boolean;
  teacher_read: boolean;
  teacher_write: boolean;
} {
  const levelFor = (role: ProgramRole): ProgramLevel => {
    let level: ProgramLevel = "hidden";
    for (const entry of programAccessEntries(program)) {
      if (audienceRole(entry.audience) !== role) continue;
      if (LEVEL_ORDER[entry.level] > LEVEL_ORDER[level]) level = entry.level;
    }
    return level;
  };
  const writes = (level: ProgramLevel) =>
    level === "contribute" || level === "manage";
  const advisorLevel = levelFor("advisor");
  const teacherLevel = levelFor("teacher");
  return {
    advisor_read: advisorLevel !== "hidden",
    advisor_write: writes(advisorLevel),
    teacher_read: teacherLevel !== "hidden",
    teacher_write: writes(teacherLevel),
  };
}

/**
 * Whether this student could be in this population, as far as a roster summary
 * can tell.
 *
 * A fact the summary does not carry is not held against the student -- the
 * failure that matters here is hiding a program from somebody it concerns, not
 * listing one with an empty table. And a roster-kept population (a team) is
 * judged on school, grade and cohort only: who is on the team lives in the
 * program's record, which would be a read per program to consult.
 */
function couldBeInPopulation(
  population: ProgramPopulation,
  student: RelatedStudent | undefined,
): boolean {
  if (!student) return true;
  const { school_id, grade_level, cohort } = population;
  if (school_id && student.school_id && student.school_id !== school_id)
    return false;
  if (
    grade_level?.length &&
    student.grade_level !== undefined &&
    !grade_level.includes(student.grade_level)
  )
    return false;
  if (cohort?.length && student.cohort && !cohort.includes(student.cohort))
    return false;
  return true;
}

/**
 * The viewer as one program sees them: taught and advised students narrowed to
 * the ones the program could include.
 *
 * So `isRole`, the ceiling and the row scope all ask "do they teach anybody
 * *here*", with no change to any of them. Costs nothing -- the summaries are on
 * the roster the viewer was built from.
 */
export function viewerForPopulation(
  viewer: ProgramViewer,
  population: ProgramPopulation | undefined,
): ProgramViewer {
  const students = viewer.students;
  if (!population || !students) return viewer;
  const narrow = (ids: readonly string[] | undefined) =>
    ids?.filter((id) => couldBeInPopulation(population, students[id]));
  return {
    ...viewer,
    taughtStudentIds: narrow(viewer.taughtStudentIds),
    advisedStudentIds: narrow(viewer.advisedStudentIds),
  };
}

/** A program's access block, or the legacy default when it has none. */
export function programAccessEntries(
  program: Pick<Program, "access">,
): ProgramAccessEntry[] {
  return program.access?.length ? program.access : DEFAULT_PROGRAM_ACCESS;
}

/**
 * What this viewer gets on this program.
 *
 * The most generous matching entry wins. Somebody can match several — an HS
 * admin who also holds the office capability — and the alternative, first-match,
 * would make the answer depend on the order rows happen to be written in.
 */
export function resolveProgramAccess(
  program: Pick<Program, "access"> & Partial<Pick<Program, "population">>,
  anyViewer: ProgramViewer,
): { level: ProgramLevel; scope: ProgramScope; roles: ProgramRole[] } {
  const viewer = viewerForPopulation(anyViewer, program.population);
  const roles: ProgramRole[] = [];
  let level: ProgramLevel = "hidden";
  let scope: ProgramScope = "own";
  for (const entry of programAccessEntries(program)) {
    const role = audienceRole(entry.audience);
    if (!role || !matchesAudience(entry.audience, viewer)) continue;
    if (!roles.includes(role)) roles.push(role);
    if (LEVEL_ORDER[entry.level] > LEVEL_ORDER[level]) {
      level = entry.level;
      scope = entry.scope;
    } else if (entry.level === level && entry.scope === "all") {
      // Same power through two roles, one of them unscoped: take the wider
      // view. An advisor who is also the office should not lose the office's
      // whole-population list by also being somebody's advisor.
      scope = "all";
    }
  }
  // And never more than this account's capabilities would survive on save.
  const ceiling = viewerCeiling(viewer);
  if (LEVEL_ORDER[level] > LEVEL_ORDER[ceiling]) level = ceiling;
  return { level, scope, roles };
}

/** Whether the program should appear to this viewer at all. */
export function canSeeProgram(
  program: Pick<Program, "access"> & Partial<Pick<Program, "population">>,
  viewer: ProgramViewer,
): boolean {
  return resolveProgramAccess(program, viewer).level !== "hidden";
}

/**
 * Whether an `own`-scoped role actually reaches this particular student.
 *
 * `office` and `administrator` are always written with `scope: "all"` in
 * practice — there is no "office's own students" relationship for an `own`
 * row to mean — so they reach every student by default here, same as they
 * always have. `advisor` and `teacher` are the two roles with a real
 * relationship to check, and that is the whole point of this function: it is
 * what `resolveProgramAccess` does **not** do, because it answers once per
 * viewer per program rather than once per viewer per student per program.
 */
function roleReachesStudent(
  role: ProgramRole,
  viewer: ProgramViewer,
  student: { student_id: string },
): boolean {
  switch (role) {
    case "advisor":
      return Boolean(viewer.advisedStudentIds?.includes(student.student_id));
    case "teacher":
      return Boolean(viewer.taughtStudentIds?.includes(student.student_id));
    case "administrator":
    case "office":
      return true;
  }
}

/**
 * What this viewer may do for **one** student on this program — the question
 * `resolveProgramAccess` cannot answer.
 *
 * `resolveProgramAccess` resolves `own` scope through `studentInProgramScope`,
 * which unions every `own`-scoped role's reach (advised ∪ taught) into one
 * set, then hands the *whole program's* resolved level to every student in
 * that union. That is correct for "which rows appear on this screen at all"
 * — an advisor should see their advisees' rows even if they also teach other
 * students with no row otherwise — but it is wrong for "what can this viewer
 * do to *this* row": a teacher who is also an advisor would get `contribute`
 * (the advisor level) for every taught student, not only their advisees,
 * because the union does not remember which role reached which student.
 *
 * This narrows `resolveProgramAccess` to only the roles that actually reach
 * `student`, before taking the highest level among them — so a criterion
 * marked `write_roles: ["advisor"]` only opens for a teacher-advisor's actual
 * advisees, never for a student they merely teach.
 *
 * Still UI gating, not the boundary: `firestore.rules` grants a whole
 * document once a role reaches the program at all (see its own doc comment),
 * so this is what decides which controls this screen *offers*, same posture
 * as `resolveProgramAccess` and deliberately so — see `docs/senior-privileges.md`
 * §6 and `docs/program-access.md` for the threat model this answers to.
 */
export function resolveProgramAccessForStudent(
  program: Pick<Program, "access">,
  viewer: ProgramViewer,
  student: { student_id: string },
): { level: ProgramLevel; roles: ProgramRole[] } {
  const roles: ProgramRole[] = [];
  let level: ProgramLevel = "hidden";
  for (const entry of programAccessEntries(program)) {
    const role = audienceRole(entry.audience);
    if (!role || !matchesAudience(entry.audience, viewer)) continue;
    if (entry.scope === "own" && !roleReachesStudent(role, viewer, student))
      continue;
    if (!roles.includes(role)) roles.push(role);
    if (LEVEL_ORDER[entry.level] > LEVEL_ORDER[level]) level = entry.level;
  }
  const ceiling = viewerCeiling(viewer);
  if (LEVEL_ORDER[level] > LEVEL_ORDER[ceiling]) level = ceiling;
  return { level, roles };
}

/**
 * Whether this viewer may place or lift a hold on **any** criterion here.
 *
 * For a heading, not for a control: which holds they may touch is per
 * criterion, like every other entry on the screen. See `canEnterCriterion`.
 */
export function canManageAnyHold(
  criteria: Pick<Criterion, "kind" | "write_roles">[],
  access: { level: ProgramLevel; roles?: ProgramRole[] },
): boolean {
  return criteria.some(
    (criterion) =>
      criterion.kind === "no_hold" && canEnterCriterion(criterion, access),
  );
}

/**
 * Who may enter one criterion — tick its box, or place and lift its hold.
 *
 * **One rule for both, because a hold is a criterion like any other.** There is
 * no reason "no discipline hold" and "advisor has not flagged a concern" should
 * have different permission machinery: both are a human deciding something
 * about one student, and which humans may decide it belongs to the criterion
 * the policy author wrote. An advisor hold is a `no_hold` criterion whose
 * `write_roles` name the advisor, and nothing else has to change for it.
 *
 * `manage` enters anything — an assistant principal answering a student at the
 * desk should not be blocked by a box somebody marked as the advisor's, and as
 * a rule an administrator can do whatever an advisor can. Below that, the
 * policy decides per criterion.
 *
 * This is where "seven checkboxes would mean seven kinds of contributor" is
 * answered: a criterion names one or more of a fixed four, so "who may enter
 * this?" has four possible answers on every policy in the school rather than as
 * many as somebody invented.
 */
export function canEnterCriterion(
  criterion: Pick<Criterion, "kind" | "write_roles">,
  access: { level: ProgramLevel; roles?: ProgramRole[] },
): boolean {
  if (access.level === "manage") return true;
  if (access.level !== "contribute") return false;
  const owners = criterionRoles(criterion);
  return (access.roles ?? []).some((role) => owners.includes(role));
}

/**
 * Which roles own one criterion.
 *
 * The fallback depends on the kind, and neither half is a limit — a policy that
 * says otherwise is obeyed. A **checkbox** nobody claimed is the office's,
 * which is the conservative reading of paperwork; a **hold** nobody claimed is
 * the administrator's, because a hold is what takes the privilege away and
 * that is the sane thing for a policy author who has not thought about it yet
 * to get. An **exception** to a measure is the mirror of a hold — it gives the
 * privilege back despite the rule — and defaults the same way. Both are
 * starting points, not rules.
 */
export function criterionRoles(
  criterion: Pick<Criterion, "kind" | "write_roles">,
): ProgramRole[] {
  if (criterion.write_roles?.length) return criterion.write_roles;
  // A checkbox is paperwork. A hold takes the privilege away and an exception
  // hands it back despite the rule, so both start with whoever runs it.
  return criterion.kind === "flag" ? ["office"] : ["administrator"];
}

/**
 * Whether a status row's `advisor` names this staff member.
 *
 * **No longer the access gate.** This matched the denormalized
 * `homeroom_advisor` string by name, which is what used to decide whether an
 * advisor could see a student at all (`studentInProgramScope` below). That
 * gate is now `advisedStudentIds`, derived from the staff member's own
 * Advisory section in Aspen -- a real id-based roster, not a name match
 * against free text. This function is kept because the name match is still
 * useful for *display* (e.g. showing whose advisory a student is in) and
 * because it documents the encoding Aspen actually sends, which cost a
 * production bug to get right the first time:
 *
 * The field is `homeroom_advisor` straight out of Aspen, denormalized onto the
 * status row by the evaluator, and it is **not one name**. Observed on the
 * student detail page, which renders the same field:
 *
 *     Hinkle, T
 *     Humphreys, A; Benedetti, S; Andrews, M; Linsner, E
 *
 * So: surname, comma, first initial — and a **semicolon-separated list** when a
 * student has several advisors, which an advisory of four staff evidently does.
 * Both of those were wrong in the first version of this function, which took
 * the repo's fixtures ("Okonkwo") as evidence of the encoding. They are not
 * evidence; `AGENTS.md` says so, and #33 is the standing example of a feature
 * that passed every test and matched nothing in production for exactly this
 * reason.
 *
 * The surviving looseness is the initial. "Hinkle, T" cannot tell two staff
 * named Hinkle whose first names start with T apart, because the field does not
 * carry enough to do it. The full-name spellings are accepted too, in case some
 * rows carry them; a bare surname is accepted for the same reason, and is the
 * loosest of the lot.
 */
export function advisorNamesStaff(
  advisor: string | undefined,
  staff: { first_name?: string; last_name?: string } | undefined,
): boolean {
  const normalize = (value: string) =>
    value.toLowerCase().replace(/\./g, "").replace(/\s+/g, " ").trim();
  const last = normalize(staff?.last_name ?? "");
  if (!advisor || !last) return false;
  const first = normalize(staff?.first_name ?? "");
  const spellings = new Set([last]);
  if (first) {
    spellings.add(`${last}, ${first}`);
    spellings.add(`${first} ${last}`);
    spellings.add(`${last}, ${first.charAt(0)}`);
  }
  // One student, several advisors: the whole advisory shares the field.
  return advisor.split(";").some((entry) => spellings.has(normalize(entry)));
}

/**
 * Whether this account could see any program at all.
 *
 * The cheap question a dashboard asks before offering a tile: it costs no
 * reads, because it is about the grant rather than about any policy. A `false`
 * is certain — the rules would refuse the rollup — while a `true` only means
 * "worth offering", since an individual program may still not name them.
 */
export function hasAnyProgramAccess(viewer: ProgramViewer): boolean {
  return viewerCeiling(viewer) !== "hidden";
}

/**
 * Whether one student is inside a viewer's scope for a program.
 *
 * Takes `advisedStudentIds` and `taughtStudentIds` -- both derived from the
 * viewer's own roster document, so this costs no per-student reads -- rather
 * than matching the denormalized `advisor` string by name. `student.advisor`
 * is still accepted on the input shape so callers that only have the status
 * row's fields keep compiling, but it is no longer read here.
 */
export function studentInProgramScope(
  access: { scope: ProgramScope },
  viewer: ProgramViewer,
  student: { student_id: string; advisor?: string },
): boolean {
  if (access.scope === "all") return true;
  if (viewer.advisedStudentIds?.includes(student.student_id)) return true;
  return Boolean(viewer.taughtStudentIds?.includes(student.student_id));
}
